import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AuthorizationDialog } from '@/components/ui/authorization-dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { ArrowLeft, Search, Upload, Trash2, FileText, Eye, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TechPack() {
  const navigate = useNavigate();
  const [designs, setDesigns] = useState([]);
  const [filteredDesigns, setFilteredDesigns] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewDetails, setViewDetails] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadingDesignId, setUploadingDesignId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [designToDeletePdf, setDesignToDeletePdf] = useState(null);
  const [viewPdfDesign, setViewPdfDesign] = useState(null);
  const [samplingTimeData, setSamplingTimeData] = useState(null);
  const [zoomImage, setZoomImage] = useState(null);
  const [zoomImageOpen, setZoomImageOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [viewPdfData, setViewPdfData] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ALL');

  useEffect(() => {
    fetchApprovedDesigns();
  }, []);

  useEffect(() => {
    filterDesigns();
  }, [searchQuery, designs, statusFilter]);

  const fetchApprovedDesigns = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/designs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter only APPROVED designs
      const approvedDesigns = response.data.filter(d => d.status === 'APPROVED');
      setDesigns(approvedDesigns);
      setFilteredDesigns(approvedDesigns);
    } catch (error) {
      console.error('Failed to fetch approved designs:', error);
      toast.error('Failed to load designs');
    }
  };

  // Auto-refresh every 5 seconds, but pause when viewing or uploading
  const isUserEditing = !!viewDetails || uploadingPdf || deleteDialogOpen || showPdfViewer;
  useAutoRefresh(fetchApprovedDesigns, 5, isUserEditing);

  const filterDesigns = () => {
    let filtered = designs;

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(design =>
        design.article_code?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(design => {
        const status = getTechPackStatus(design);
        return status === statusFilter;
      });
    }

    setFilteredDesigns(filtered);
  };

  const handleViewDesign = async (design) => {
    try {
      const token = localStorage.getItem('token');
      // Fetch fresh data with live enrichment
      const response = await axios.get(`${API}/designs/${design.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const enrichedDesign = await enrichWithLiveData(response.data);
      setViewDetails(enrichedDesign);
      
      // Fetch sampling time data for this article (Machine Operator Data)
      try {
        const samplingResponse = await axios.get(`${API}/sampling-time-tracker/by-article/${design.article_code}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSamplingTimeData(samplingResponse.data);
      } catch (error) {
        console.error('Error fetching sampling time data:', error);
        setSamplingTimeData(null);
      }
    } catch (error) {
      console.error('Error fetching design details:', error);
      toast.error('Failed to load design details');
    }
  };

  const enrichWithLiveData = async (design) => {
    try {
      const token = localStorage.getItem('token');
      const enrichedDesign = { ...design };

      // Fetch live fabric data
      if (design.fabrics && design.fabrics.length > 0) {
        const fabricsResponse = await axios.get(`${API}/fabric-master`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        enrichedDesign.fabrics = design.fabrics.map(fabric => {
          const masterFabric = fabricsResponse.data.find(f => f.ln_code === fabric.fabric_code);
          if (masterFabric) {
            const masterColor = masterFabric.colors?.find(c => c.color_code === fabric.color_code);
            if (masterColor) {
              return {
                ...fabric,
                color_name: masterColor.color_name,
                width: masterFabric.width,
                gsm: masterFabric.gsm,
                composition: masterFabric.composition,
                fabric_type: masterFabric.fabric_type,
                price_per_meter: masterColor.price_per_meter
              };
            }
          }
          return fabric;
        });
      }

      // Fetch live trim data
      if (design.trims && design.trims.length > 0) {
        const trimsResponse = await axios.get(`${API}/trims-master`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const packagingResponse = await axios.get(`${API}/packaging-master`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        enrichedDesign.trims = design.trims.map(trim => {
          let masterTrim = trimsResponse.data.find(t => t.ln_z_code === trim.trim_code);
          if (!masterTrim) {
            masterTrim = packagingResponse.data.find(p => p.ln_y_code === trim.trim_code);
          }
          
          if (masterTrim) {
            const masterColor = masterTrim.colors?.find(c => c.color_code === trim.color_code);
            if (masterColor) {
              return {
                ...trim,
                color_name: masterColor.color_name,
                price: masterColor.price
              };
            }
          }
          return trim;
        });
      }

      return enrichedDesign;
    } catch (error) {
      console.error('Error enriching with live data:', error);
      return design;
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Please select a PDF file');
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setPdfPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadPdf = async () => {
    if (!selectedFile || !viewDetails) return;

    setUploadingPdf(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(
        `${API}/designs/${viewDetails.id}/upload-techpack`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('Tech Pack uploaded successfully');
      setPdfPreview(null);
      setSelectedFile(null);
      fetchApprovedDesigns();
      
      // Update viewDetails with new PDF URL
      const updatedDesign = { ...viewDetails, tech_pack_pdf: response.data.url };
      setViewDetails(updatedDesign);
    } catch (error) {
      console.error('Failed to upload tech pack:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload tech pack');
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleDeletePdf = (design) => {
    setDesignToDeletePdf(design);
    setDeleteDialogOpen(true);
  };

  const confirmDeletePdf = async () => {
    if (!designToDeletePdf) return;

    try {
      // Update viewDetails to remove PDF
      setViewDetails({ ...designToDeletePdf, tech_pack_pdf: null });
      toast.success('PDF removed. Click Update to save changes.');
      setDeleteDialogOpen(false);
      setDesignToDeletePdf(null);
    } catch (error) {
      console.error('Failed to delete tech pack:', error);
      toast.error('Failed to delete tech pack');
    }
  };

  const getTechPackStatus = (design) => {
    return design.tech_pack_pdf ? 'Completed' : 'Pending';
  };

  const handleDownloadImage = (imageData, filename) => {
    try {
      // Create a link element
      const link = document.createElement('a');
      link.href = imageData;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Image downloaded successfully');
    } catch (error) {
      console.error('Error downloading image:', error);
      toast.error('Failed to download image');
    }
  };

  const handleViewPdf = async () => {
    if (!viewDetails?.tech_pack_pdf) return;
    
    try {
      const response = await fetch(viewDetails.tech_pack_pdf);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        setViewPdfData(reader.result);
        setShowPdfViewer(true);
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Failed to load PDF:', error);
      toast.error('Failed to load PDF');
    }
  };

  const handleUpdate = async () => {
    if (!viewDetails) return;

    try {
      const token = localStorage.getItem('token');

      // If there's a pending PDF upload, upload it first
      if (selectedFile && pdfPreview) {
        await handleUploadPdf();
      } else {
        // Update the design with current viewDetails (handles PDF deletion or other changes)
        await axios.put(`${API}/designs/${viewDetails.id}`, viewDetails, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Tech Pack updated successfully');
      }
      
      // Refresh the designs list
      await fetchApprovedDesigns();
      
      // Close the dialog and clear state
      setViewDetails(null);
      setPdfPreview(null);
      setSelectedFile(null);
      setViewPdfData(null);
    } catch (error) {
      console.error('Failed to update tech pack:', error);
      toast.error('Failed to update tech pack');
    }
  };

  // Inline grid actions
  const handleInlineFileSelect = (designId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        if (!file.type.includes('pdf')) {
          toast.error('Please select a PDF file');
          return;
        }
        handleInlineUploadPdf(designId, file);
      }
    };
    input.click();
  };

  const handleInlineUploadPdf = async (designId, file) => {
    setUploadingPdf(true);
    setUploadingDesignId(designId);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/designs/${designId}/upload-techpack`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Tech Pack PDF uploaded successfully');
      fetchApprovedDesigns();
    } catch (error) {
      console.error('Failed to upload PDF:', error);
      toast.error('Failed to upload PDF');
    } finally {
      setUploadingPdf(false);
      setUploadingDesignId(null);
    }
  };

  const handleInlineViewPdf = async (design) => {
    if (!design.tech_pack_pdf) return;
    
    try {
      const response = await fetch(design.tech_pack_pdf);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        setViewPdfData(reader.result);
        setViewPdfDesign(design);
        setShowPdfViewer(true);
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Failed to load PDF:', error);
      toast.error('Failed to load PDF');
    }
  };

  const handleInlineDownloadPdf = (design) => {
    if (design.tech_pack_pdf) {
      const link = document.createElement('a');
      link.href = design.tech_pack_pdf;
      link.download = `${design.article_code}_techpack.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleInlineDeletePdfClick = (design) => {
    setDesignToDeletePdf(design);
    setDeleteDialogOpen(true);
  };

  const confirmInlineDeletePdf = async () => {
    if (!designToDeletePdf) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/designs/${designToDeletePdf.id}/techpack`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Tech Pack PDF deleted successfully');
      setDeleteDialogOpen(false);
      setDesignToDeletePdf(null);
      fetchApprovedDesigns();
    } catch (error) {
      console.error('Failed to delete PDF:', error);
      toast.error('Failed to delete PDF');
    }
  };


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/design-studio')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Tech Pack
              </h1>
              <p className="text-sm text-gray-600">Manage technical packages for approved designs</p>
            </div>
          </div>
        </div>
      </header>

      {/* Search and Filters */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Enter LN-X code or scan barcode"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <SearchableSelect
            options={[
              { value: 'ALL', label: 'All Status' },
              { value: 'PENDING', label: 'Pending' },
              { value: 'COMPLETED', label: 'Completed' }
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="Filter by status"
          />
        </div>

        {/* Grid */}
        {filteredDesigns.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">No approved designs found</p>
            <p className="text-sm text-gray-500 mt-2">
              Tech packs are available for designs with "Approved" status
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredDesigns.map((design) => {
              const status = getTechPackStatus(design);
              return (
                <div
                  key={design.id}
                  className="bg-white border-2 border-black p-4 hover:shadow-lg transition-shadow"
                >
                  <div 
                    className="flex justify-between items-start mb-3 cursor-pointer"
                    onClick={() => handleViewDesign(design)}
                  >
                    <div className="flex-1">
                      <h3 className="font-bold text-lg">{design.article_code}</h3>
                      <p className="text-sm text-gray-600">{design.article_name}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-bold rounded ${
                      status === 'Completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {status}
                    </span>
                  </div>

                  <div className="flex gap-2 mt-4" onClick={(e) => e.stopPropagation()}>
                    {!design.tech_pack_pdf ? (
                      <Button
                        onClick={() => handleInlineFileSelect(design.id)}
                        size="sm"
                        className="flex-1 bg-black hover:bg-gray-800"
                        disabled={uploadingPdf && uploadingDesignId === design.id}
                      >
                        <Upload className="w-4 h-4 mr-1" />
                        {uploadingPdf && uploadingDesignId === design.id ? 'Uploading...' : 'Upload PDF'}
                      </Button>
                    ) : (
                      <>
                        <Button
                          onClick={() => handleInlineViewPdf(design)}
                          size="sm"
                          variant="outline"
                          className="flex-1"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <Button
                          onClick={() => handleInlineDownloadPdf(design)}
                          size="sm"
                          variant="outline"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleInlineDeletePdfClick(design)}
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* View Dialog */}
      <Dialog open={!!viewDetails} onOpenChange={() => { 
        setViewDetails(null); 
        setPdfPreview(null); 
        setSelectedFile(null);
        setSamplingTimeData(null);
        setViewPdfData(null);
      }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Tech Pack - {viewDetails?.article_code}</DialogTitle>
          </DialogHeader>
          {viewDetails && (
            <div className="space-y-4">
              {/* Article Section */}
              <div className="p-5 border-2 border-black">
                <p className="text-xs font-semibold tracking-wider text-gray-500 mb-4">ARTICLE</p>
                <div className="flex justify-between items-start">
                  <div className="grid grid-cols-2 gap-4 flex-1">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-1">CODE</p>
                      <p className="text-sm font-bold">{viewDetails.article_code}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-1">CATEGORY</p>
                      <p className="text-sm">{viewDetails.category || '-'}</p>
                    </div>
                  </div>
                  {/* Barcode */}
                  {viewDetails.article_barcode && (
                    <div className="ml-6 flex-shrink-0">
                      <img 
                        src={viewDetails.article_barcode} 
                        alt="Barcode" 
                        className="h-16 object-contain"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Fabrics */}
              {viewDetails.fabrics && viewDetails.fabrics.length > 0 && (
                <div className="p-5 border-2 border-black">
                  <p className="text-xs font-semibold tracking-wider text-gray-500 mb-4">FABRICS</p>
                  <div className="space-y-3">
                    {viewDetails.fabrics.map((fabric, index) => (
                      <div key={index} className="p-4 border border-gray-300 bg-gray-50">
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1">CODE</p>
                            <p className="text-sm font-bold">{fabric.fabric_code}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1">COLOR</p>
                            <p className="text-sm">{fabric.color_code} - {fabric.color_name}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1">CONSUMPTION</p>
                            <p className="text-sm">{fabric.consumption} {parseFloat(fabric.consumption) > 1 ? 'Meters' : 'Meter'}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1">COMPOSITION</p>
                            <p className="text-sm">{fabric.composition || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1">WIDTH</p>
                            <p className="text-sm">{fabric.width}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1">GSM</p>
                            <p className="text-sm">{fabric.gsm}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Digital Design */}
              {viewDetails.digital_design_code && (
                <div className="p-5 border-2 border-black">
                  <p className="text-xs font-semibold tracking-wider text-gray-500 mb-4">DIGITAL DESIGN</p>
                  <div className="p-4 border border-gray-300 bg-gray-50">
                    <div className="grid grid-cols-3 gap-6">
                      <div className="col-span-2 grid grid-cols-2 gap-6">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1">CODE</p>
                          <p className="text-sm font-bold">{viewDetails.digital_design_code}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1">COLORWAY</p>
                          <p className="text-sm">{viewDetails.digital_design_colorway || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1">TYPE</p>
                          <p className="text-sm">{viewDetails.digital_design_type || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1">CONSUMPTION</p>
                          <p className="text-sm">{viewDetails.digital_design_consumption ? `${viewDetails.digital_design_consumption} ${parseFloat(viewDetails.digital_design_consumption) > 1 ? 'Meters' : 'Meter'}` : '-'}</p>
                        </div>
                      </div>
                      <div className="col-span-1">
                        {viewDetails.digital_design_colorway_image && (
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-gray-500">COLORWAY IMAGE</p>
                            <img 
                              src={viewDetails.digital_design_colorway_image} 
                              alt="Colorway" 
                              className="w-auto h-[120px] object-contain rounded cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => { setZoomImage(viewDetails.digital_design_colorway_image); setZoomImageOpen(true); }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Trims */}
              {viewDetails.trims && viewDetails.trims.length > 0 && (
                <div className="p-5 border-2 border-black">
                  <p className="text-xs font-semibold tracking-wider text-gray-500 mb-4">TRIMS & ACCESSORIES</p>
                  <div className="space-y-2">
                    {viewDetails.trims.map((trim, index) => (
                      <div key={index} className="flex gap-4 items-center p-3 border border-gray-300">
                        <span className="text-sm font-bold w-32">{trim.trim_code}</span>
                        <span className="flex-1 text-sm">{trim.color_code} - {trim.color_name}</span>
                        <span className="text-sm font-bold">QTY {trim.quantity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* POM */}
              {viewDetails.poms && viewDetails.poms.length > 0 && (
                <div className="p-5 border-2 border-black">
                  <p className="text-xs font-semibold tracking-wider text-gray-500 mb-4">POINTS OF MEASUREMENT</p>
                  <div className="space-y-2">
                    {viewDetails.poms.map((pom, index) => (
                      <div key={index} className="flex gap-4 items-center p-3 border border-gray-300 bg-gray-50">
                        <span className="text-sm font-bold w-24">{pom.size}</span>
                        <span className="flex-1 text-sm">{pom.point_name}</span>
                        <span className="text-sm font-bold">{pom.measurement} {pom.uom}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Machine Operator Data */}
              {samplingTimeData && (
                <div className="p-5 border-2 border-black">
                  <p className="text-xs font-semibold tracking-wider text-gray-500 mb-4">MACHINE OPERATOR DATA</p>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="p-3 bg-gray-50 border border-gray-300 rounded">
                      <p className="text-xs font-semibold text-gray-500 mb-1">SAMPLING TIME</p>
                      <p className="text-sm font-medium">
                        {Math.floor(samplingTimeData.total_time_seconds / 60)} min
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 border border-gray-300 rounded">
                      <p className="text-xs font-semibold text-gray-500 mb-1">EST. PRODUCTION TIME</p>
                      <p className="text-sm font-medium">
                        {Math.floor(samplingTimeData.total_time_seconds * 0.65 / 60)} min
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 border border-gray-300 rounded">
                      <p className="text-xs font-semibold text-gray-500 mb-1">SAMPLER</p>
                      <p className="text-sm font-medium">{samplingTimeData.sampler_name}</p>
                    </div>
                    <div className="p-3 bg-gray-50 border border-gray-300 rounded">
                      <p className="text-xs font-semibold text-gray-500 mb-1">COMPLETION DATE</p>
                      <p className="text-sm font-medium">
                        {samplingTimeData.end_time ? new Date(samplingTimeData.end_time).toLocaleDateString() : '-'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Design Images */}
              <div className="p-5 border-2 border-black">
                <p className="text-xs font-semibold tracking-wider text-gray-500 mb-4">DESIGN IMAGES</p>
                <div className="grid grid-cols-2 gap-4">
                  {viewDetails.inspiration_image_path && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-2">INSPIRATION</p>
                      <div className="relative">
                        <img 
                          src={`${API}/${viewDetails.inspiration_image_path}`} 
                          alt="Inspiration" 
                          className="w-full h-64 object-contain border border-gray-300 bg-gray-50 rounded cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => { setZoomImage(`${API}/${viewDetails.inspiration_image_path}`); setZoomImageOpen(true); }}
                        />
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownloadImage(`${API}/${viewDetails.inspiration_image_path}`, `${viewDetails.article_code}_inspiration.png`); }}
                          className="absolute bottom-2 left-2 bg-white hover:bg-gray-100 rounded-full p-2 shadow-lg border border-gray-300 transition-colors"
                          title="Download image"
                        >
                          <Download className="w-4 h-4 text-gray-700" />
                        </button>
                      </div>
                    </div>
                  )}
                  {viewDetails.sketch_front_image_path && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-2">SKETCH FRONT</p>
                      <div className="relative">
                        <img 
                          src={`${API}/${viewDetails.sketch_front_image_path}`} 
                          alt="Sketch Front" 
                          className="w-full h-64 object-contain border border-gray-300 bg-gray-50 rounded cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => { setZoomImage(`${API}/${viewDetails.sketch_front_image_path}`); setZoomImageOpen(true); }}
                        />
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownloadImage(`${API}/${viewDetails.sketch_front_image_path}`, `${viewDetails.article_code}_sketch_front.png`); }}
                          className="absolute bottom-2 left-2 bg-white hover:bg-gray-100 rounded-full p-2 shadow-lg border border-gray-300 transition-colors"
                          title="Download image"
                        >
                          <Download className="w-4 h-4 text-gray-700" />
                        </button>
                      </div>
                    </div>
                  )}
                  {viewDetails.sketch_back_image_path && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-2">SKETCH BACK</p>
                      <div className="relative">
                        <img 
                          src={`${API}/${viewDetails.sketch_back_image_path}`} 
                          alt="Sketch Back" 
                          className="w-full h-64 object-contain border border-gray-300 bg-gray-50 rounded cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => { setZoomImage(`${API}/${viewDetails.sketch_back_image_path}`); setZoomImageOpen(true); }}
                        />
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownloadImage(`${API}/${viewDetails.sketch_back_image_path}`, `${viewDetails.article_code}_sketch_back.png`); }}
                          className="absolute bottom-2 left-2 bg-white hover:bg-gray-100 rounded-full p-2 shadow-lg border border-gray-300 transition-colors"
                          title="Download image"
                        >
                          <Download className="w-4 h-4 text-gray-700" />
                        </button>
                      </div>
                    </div>
                  )}
                  {viewDetails.finished_good_image_path && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-2">FINISHED GOOD</p>
                      <div className="relative">
                        <img 
                          src={`${API}/${viewDetails.finished_good_image_path}`} 
                          alt="Finished Good" 
                          className="w-full h-64 object-contain border border-gray-300 bg-gray-50 rounded cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => { setZoomImage(`${API}/${viewDetails.finished_good_image_path}`); setZoomImageOpen(true); }}
                        />
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownloadImage(`${API}/${viewDetails.finished_good_image_path}`, `${viewDetails.article_code}_finished_good.png`); }}
                          className="absolute bottom-2 left-2 bg-white hover:bg-gray-100 rounded-full p-2 shadow-lg border border-gray-300 transition-colors"
                          title="Download image"
                        >
                          <Download className="w-4 h-4 text-gray-700" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {!viewDetails.inspiration_image_path && !viewDetails.sketch_front_image_path && !viewDetails.sketch_back_image_path && !viewDetails.finished_good_image_path && (
                  <p className="text-sm text-gray-400 text-center py-4">No design images available</p>
                )}
              </div>

              {/* Comments */}
              {viewDetails.comments && (
                <div className="p-5 border-2 border-black">
                  <p className="text-xs font-semibold tracking-wider text-gray-500 mb-4">COMMENTS</p>
                  <p className="text-sm whitespace-pre-wrap">{viewDetails.comments}</p>
                </div>
              )}

              {/* Tech Pack Upload/View Section */}
              <div className="p-5 border-2 border-black bg-blue-50">
                <div className="flex justify-between items-center mb-4">
                  <p className="text-xs font-semibold tracking-wider text-gray-500">TECH PACK PDF</p>
                  <span className={`text-xs font-bold px-3 py-1 rounded ${viewDetails.tech_pack_pdf ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {viewDetails.tech_pack_pdf ? 'COMPLETE' : 'PENDING'}
                  </span>
                </div>
                
                {viewDetails.tech_pack_pdf ? (
                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <Button onClick={handleViewPdf} className="flex-1">
                        <Eye className="w-4 h-4 mr-2" /> View Tech Pack
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = viewDetails.tech_pack_pdf;
                          link.download = `${viewDetails.article_code}_techpack.pdf`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                      >
                        <Download className="w-4 h-4 mr-2" /> Download
                      </Button>
                      <Button variant="destructive" onClick={() => {
                        setDesignToDeletePdf(viewDetails);
                        setDeleteDialogOpen(true);
                      }}>
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Input
                      type="file"
                      accept="application/pdf"
                      onChange={handleFileSelect}
                      className="border-0 p-0 overflow-visible file:mr-4 file:py-1.5 file:px-3 file:rounded file:border-2 file:border-black file:text-xs file:font-semibold file:bg-white file:text-black hover:file:bg-gray-100 file:cursor-pointer"
                    />
                    {pdfPreview && (
                      <div className="space-y-2">
                        <p 
                          className="text-sm font-semibold underline cursor-pointer hover:text-blue-600"
                          onClick={() => setShowPdfViewer(true)}
                        >
                          Preview: {selectedFile?.name}
                        </p>
                        <div className="flex gap-2">
                          <Button onClick={handleUploadPdf} disabled={uploadingPdf} className="flex-1">
                            <Upload className="w-4 h-4 mr-2" /> {uploadingPdf ? 'Uploading...' : 'Upload Tech Pack'}
                          </Button>
                          <Button variant="outline" onClick={() => { setPdfPreview(null); setSelectedFile(null); }}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Update Button */}
              <div className="flex justify-end pt-4 border-t-2 border-gray-200">
                <Button onClick={handleUpdate} className="bg-black hover:bg-gray-800">
                  Update Tech Pack
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Zoom Dialog */}
      <Dialog open={zoomImageOpen} onOpenChange={setZoomImageOpen}>
        <DialogContent className="max-w-6xl max-h-[95vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Image Preview</DialogTitle>
          </DialogHeader>
          {zoomImage && (
            <div className="flex justify-center">
              <img src={zoomImage} alt="Zoomed" className="max-w-full h-auto" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF Viewer Dialog */}
      <Dialog open={showPdfViewer} onOpenChange={(open) => {
        setShowPdfViewer(open);
        if (!open) {
          setViewPdfData(null);
          setViewPdfDesign(null);
        }
      }}>
        <DialogContent className="max-w-screen max-h-screen w-screen h-screen p-0 m-0 rounded-none border-0">
          <DialogHeader className="absolute top-0 left-0 right-0 z-10 px-6 py-3 bg-white border-b">
            <div className="flex items-center justify-between">
              <DialogTitle>
                {pdfPreview 
                  ? `Preview: ${selectedFile?.name}` 
                  : `Tech Pack PDF - ${viewDetails?.article_code || viewPdfDesign?.article_code}`}
              </DialogTitle>
              <div className="flex items-center gap-2">
                {viewPdfData && !pdfPreview && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = viewPdfData;
                      link.download = `${(viewDetails?.article_code || viewPdfDesign?.article_code)}_techpack.pdf`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                  >
                    <Download className="w-4 h-4 mr-2" /> Download PDF
                  </Button>
                )}
                <button 
                  onClick={() => setShowPdfViewer(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
            </div>
          </DialogHeader>
          {(viewPdfData || pdfPreview) && (
            <iframe
              src={pdfPreview || viewPdfData}
              className="w-full h-full border-0"
              title="Tech Pack PDF"
              style={{ marginTop: '60px' }}
              type="application/pdf"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AuthorizationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onAuthorized={viewDetails ? confirmDeletePdf : confirmInlineDeletePdf}
        title="Delete Tech Pack PDF"
        description="Enter authorization code to remove this tech pack PDF."
        correctCode="tprem"
      />
    </div>
  );
}
